/**
 * SurfaceState — one primitive for loading / empty / error across every
 * Security, AI Governance, Lighthouse and Settings surface. Used inline
 * by Panel and WorkQueue, or directly inside a SurfaceShell body.
 */
import { ReactNode } from "react";
import { AlertTriangle, Inbox, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export type SurfaceStateVariant = "loading" | "empty" | "error";

interface SurfaceStateProps {
  variant: SurfaceStateVariant;
  /** Override the default headline for the variant. */
  title?: string;
  /** Override the default helper line for the variant. */
  description?: ReactNode;
  /** Raw error (Error, EdrApiError, LighthouseApiError, string, unknown). */
  error?: unknown;
  /** Retry handler — shown only when present and variant === "error". */
  onRetry?: () => void;
  /** Density. "page" pads more and shows larger icon; "panel" is inline. */
  size?: "page" | "panel" | "inline";
  /** Skeleton layout — only used for variant="loading". */
  skeleton?: "list" | "table" | "card" | "none";
  /** Optional custom icon overriding the variant default. */
  icon?: ReactNode;
  className?: string;
}

const DEFAULT_TITLE: Record<SurfaceStateVariant, string> = {
  loading: "",
  empty: "Nothing here yet",
  error: "Couldn't load this view",
};

export function SurfaceState({
  variant,
  title,
  description,
  error,
  onRetry,
  size = "panel",
  skeleton = "list",
  icon,
  className,
}: SurfaceStateProps) {
  if (variant === "loading") {
    return (
      <div className={cn("w-full", className)} aria-busy="true" aria-live="polite">
        <SkeletonBlock kind={skeleton} size={size} />
      </div>
    );
  }

  const pad = size === "page" ? "py-16" : size === "inline" ? "py-4" : "py-10";
  const iconWrap =
    variant === "error"
      ? "bg-destructive/10 text-destructive border-destructive/20"
      : "bg-muted text-muted-foreground border-border";
  const DefaultIcon = variant === "error" ? AlertTriangle : Inbox;

  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      className={cn("text-center", pad, className)}
    >
      <div
        className={cn(
          "mx-auto grid place-items-center rounded-full border",
          size === "page" ? "h-12 w-12" : "h-10 w-10",
          iconWrap,
        )}
        aria-hidden
      >
        {icon ?? <DefaultIcon className={size === "page" ? "h-5 w-5" : "h-4 w-4"} />}
      </div>
      <p
        className={cn(
          "mt-3 font-medium text-foreground",
          size === "page" ? "text-base" : "text-sm",
        )}
      >
        {title ?? DEFAULT_TITLE[variant]}
      </p>
      {(description || variant === "error") && (
        <p
          className={cn(
            "mt-1 text-muted-foreground mx-auto max-w-md leading-relaxed",
            size === "page" ? "text-sm" : "text-xs",
          )}
        >
          {description ?? (variant === "error" ? friendlyError(error) : null)}
        </p>
      )}
      {variant === "error" && onRetry && (
        <Button
          size="sm"
          variant="outline"
          onClick={onRetry}
          className="mt-4 h-7 text-xs"
        >
          <RefreshCw className="h-3 w-3 mr-1.5" /> Try again
        </Button>
      )}
    </div>
  );
}

function SkeletonBlock({
  kind,
  size,
}: {
  kind: "list" | "table" | "card" | "none";
  size: "page" | "panel" | "inline";
}) {
  if (kind === "none") return null;
  const rows = size === "inline" ? 3 : size === "page" ? 8 : 5;

  if (kind === "card") {
    return (
      <div className="space-y-3 py-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (kind === "table") {
    return (
      <div className="space-y-2 py-2">
        <div className="flex gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-3 flex-1" />
          ))}
        </div>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex gap-3">
            {Array.from({ length: 5 }).map((_, j) => (
              <Skeleton key={j} className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  // list
  return (
    <div className="space-y-2 py-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-2 w-2 rounded-full flex-none" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-2/3" />
            <Skeleton className="h-2.5 w-1/3" />
          </div>
          <Skeleton className="h-3 w-10 flex-none" />
        </div>
      ))}
    </div>
  );
}

/** Normalize anything thrown by fetch / react-query into a readable line. */
export function friendlyError(error: unknown): string {
  if (!error) return "Something went wrong reaching the backend. Please try again.";
  if (typeof error === "string") return error;
  if (error instanceof Error) {
    const msg = error.message?.trim();
    if (!msg) return "Request failed.";
    // Strip noisy fetch internals while keeping our friendly upstream copy.
    if (/^Failed to fetch$/i.test(msg) || /NetworkError/i.test(msg)) {
      return "We couldn't reach the backend. Check your connection and try again.";
    }
    return msg;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return "Unknown error.";
  }
}

/**
 * Choose the right variant from a React Query–style result.
 * Returns null when there is data to render.
 */
export function chooseSurfaceState(
  query: { isLoading?: boolean; isError?: boolean; error?: unknown },
  isEmpty: boolean,
): SurfaceStateVariant | null {
  if (query.isLoading) return "loading";
  if (query.isError) return "error";
  if (isEmpty) return "empty";
  return null;
}
